/**
 * Decision tool processor for the Now Assist Skill Plugin.
 * Handles processing of Decision tools defined in NowAssistSkillConfig.
 */

import {
    TOOL_TYPES,
    RESOURCE_MAPPING_DEFAULTS,
    RESOURCE_MAPPING_APPLICABILITY_SCRIPT,
    RESOURCE_EDGE_ERROR_HANDLER_SCRIPT,
} from '../defaults'
import type { Context, ObjectShape, Record as BuildRecord } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import { SYS_ONE_EXTEND_RESOURCE_MAPPING, SYS_ONE_EXTEND_RESOURCE_EDGE, getResourceCapability } from '../tool-utils'

/**
 * Process a Decision tool and create the resource_mapping record.
 * Edge creation is deferred to createDecisionEdges() which runs after all tools are processed.
 *
 * Decision tools create:
 * 1. sys_one_extend_resource_mapping - The main decision node record (created here)
 * 2. sys_one_extend_resource_edge - Edges FROM this decision TO each target tool (created in createDecisionEdges)
 */
export async function processDecisionTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Validate tool type
    if (tool.type !== TOOL_TYPES.DECISION) {
        diagnostics.error(arg, `Expected decision tool but got ${tool.type}`)
        return records
    }

    // Validate required fields
    if (!tool.name) {
        diagnostics.error(arg, 'Decision tool requires a name')
        return records
    }

    // Build depends_on_list from dependency names
    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    // 1. Create sys_one_extend_resource_mapping record
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            applicability_script: $.val(RESOURCE_MAPPING_APPLICABILITY_SCRIPT).toCdata(),
            depends_on_list: $.val(dependsOnList),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(getResourceCapability(tool.type)),
            resource_name: $.val(tool.name),
        })),
    })
    records.push(resourceMappingRecord)

    // Store in map for dependency resolution
    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // Create dependency edges (from depends tools TO this decision)
    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        true,
        tool._source!
    )
    records.push(...edgeRecords)

    // Note: Decision routing edges (from Decision TO targets) are created later
    // by createDecisionEdges() after all tools are processed
    return records
}

/**
 * Create edges for Decision tools after all tools have been processed.
 * This is called after all tools are in toolRecordsMap so targets can be resolved.
 */
export async function createDecisionEdges(
    decisionTools: ToolDefinition[],
    toolRecordsMap: Map<string, BuildRecord>,
    parentSkillMappingRecord: BuildRecord | undefined,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics']
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    for (const tool of decisionTools) {
        if (tool.type !== TOOL_TYPES.DECISION || !tool.name || !tool.targets) {
            continue
        }

        const decisionRecord = toolRecordsMap.get(tool.name)
        if (!decisionRecord) {
            continue
        }

        for (const targetName of tool.targets) {
            // '_end' target points to parent skill mapping (prompt)
            const isEndTarget = targetName === '_end'

            let targetRecord: BuildRecord | undefined
            if (isEndTarget) {
                targetRecord = parentSkillMappingRecord
                if (!targetRecord) {
                    diagnostics.warn(
                        arg,
                        `Decision default target '_end' requires parent skill mapping for tool '${tool.name}'`
                    )
                    continue
                }
            } else {
                targetRecord = toolRecordsMap.get(targetName)
                if (!targetRecord) {
                    diagnostics.warn(arg, `Decision target '${targetName}' not found for tool '${tool.name}'`)
                    continue
                }
            }

            // Find branch condition for this target
            const branch = tool.branches?.find((b) => b.target === targetName)
            const isDefault = tool.default === targetName

            // Determine branch name and order
            // Branches get order starting from 1, default gets order 1000 (platform convention)
            const branchIndex = tool.branches?.findIndex((b) => b.target === targetName) ?? -1
            const edgeName = isDefault ? 'Default branch' : branch?.name || `Branch ${branchIndex + 1}`
            const edgeOrder = isDefault ? 1000 : branchIndex >= 0 ? branchIndex + 1 : 0

            // For conditional branches, use conditionType to determine applicability_type:
            // - 'script' → applicability_type='script', condition goes in applicability_script
            // - 'filter' → applicability_type='condition_expression', condition goes in applicability_condition_expression
            // For default branch, leave applicability_type empty
            const hasCondition = branch?.condition && !isDefault
            const isScriptCondition = hasCondition && branch?.conditionType === 'script'
            // For internal_name, use actual skill name when pointing to prompt (parent skill)
            const parentSkillName = parentSkillMappingRecord?.get('resource_name')?.getValue() as string | undefined
            const internalNameTarget = isEndTarget ? parentSkillName || 'prompt' : targetName

            const edgeRecord = await factory.createRecord({
                source: tool._source!,
                table: SYS_ONE_EXTEND_RESOURCE_EDGE,
                properties: arg.transform(({ $ }) => ({
                    ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_EDGE, $),
                    ...(isScriptCondition ? { applicability_script: $.val(branch.condition).toCdata() } : {}),
                    applicability_type: $.val(
                        hasCondition ? (isScriptCondition ? 'script' : 'condition_expression') : ''
                    ),
                    ...(isScriptCondition
                        ? {}
                        : { applicability_condition_expression: $.val(hasCondition ? branch.condition : '') }),
                    error_handler_script: $.val(RESOURCE_EDGE_ERROR_HANDLER_SCRIPT).toCdata(),
                    from_resource: $.val(decisionRecord.getId()),
                    internal_name: $.val(`${tool.name}__${internalNameTarget}`),
                    name: $.val(edgeName),
                    order: $.val(edgeOrder),
                    to_resource: $.val(targetRecord.getId()),
                })),
            })
            records.push(edgeRecord)
        }
    }

    return records
}
